import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { setBeeEditorOpen } from '../../../redux/reducers/coreSlice';

// Dark mode for the BEE builder UI (sidebar, tiles, tabs, inputs, modals).
//
// BEE renders in a cross-origin iframe, so the only way in is its `customCss` option. The
// builder's own stylesheet (app.getbee.io/managed/<ver>/static/css/main.*.css) drives almost
// every color through custom properties on :root, in two layers:
//   1. semantic tokens (--surface-*, --border, --surface-on-top*) declared by BEE itself;
//   2. component tokens (--tile-*, --widget-*, --inputs-*, tabs, ...) that BEE injects at
//      runtime from the Beefree SDK Console theme — our account's theme pins some of these to
//      light literals (e.g. white tiles), so remapping layer 1 alone isn't enough.
// `:root:root` outranks both of BEE's own `:root` blocks regardless of load order.
//
// The email canvas is deliberately NOT touched: it must keep showing the message exactly as
// recipients will receive it.
//
// Light mode never sees any of this — callers only append/wrap when the app is in dark mode,
// so the light-mode customCss value stays byte-for-byte what it was.
export const BEE_DARK_CSS = `
:root:root {
  --surface-white: #16181d;
  --surface-5: #1c1f26;
  --surface-10: #1c1f26;
  --surface-20: #262a31;
  --surface-30: #30343c;
  --surface-50: #4a505a;
  --surface-on-top: rgba(255, 255, 255, 0.87);
  --surface-on-top-subtle: rgba(255, 255, 255, 0.6);
  --surface-on-top-hover: #ffffff;
  --surface-on-top-subtle-hover: rgba(255, 255, 255, 0.87);
  --border: #30343c;
  --border-dark: #4a505a;

  /* SDK Console theme tokens */
  --widget-bar-border: #30343c;
  --widget-bar-and-active-tab-background-color: #1c1f26;
  --active-tab-text-and-icon-color: rgba(255, 255, 255, 0.87);
  --not-active-tabs-text-and-icon-color: rgba(255, 255, 255, 0.6);
  --not-active-tabs-background-color: #16181d;
  --not-active-tabs-text-and-icon-color-hover: rgba(255, 255, 255, 0.87);
  --tile-background-color: #1c1f26;
  --tile-border-color: #30343c;
  --tile-hover-box-shadow: 0 6px 10px rgba(0, 0, 0, 0.45);
  --tile-icons-base-color: rgba(255, 255, 255, 0.87);
  --empty-rows-base-color: rgba(255, 255, 255, 0.6);
  --empty-rows-dark-color: #4a505a;
  --empty-rows-light-color: #262a31;
  --properties-panel-title-background: #1c1f26;
  --properties-panel-title-border: #30343c;
  --properties-panel-text-icons-color: rgba(255, 255, 255, 0.87);
  --widget-section-title-background: #1c1f26;
  --widget-section-title-color: rgba(255, 255, 255, 0.87);
  --widget-border-bottom-color: #30343c;
  --widget-label-color: rgba(255, 255, 255, 0.87);
  --inputs-border-color: #30343c;
  --inputs-background-color: #1c1f26;
  --inputs-text-color: rgba(255, 255, 255, 0.87);
  --widget-background-toggle-off: #4a505a;
  --widget-background-switch-off: #ffffff;
  --widget-background-switch-on: #ffffff;
  --icon-organizer-border-color: #30343c;
  --icon-organizer-thumbnail-background: #262a31;
  --icon-organizer-panel-background-color: #1c1f26;
  /* icon buttons (undo/redo/history), compact labels and preview handles take the theme's
     inputs background, which our SDK Console theme pins to white */
  --button-variant-icon-background-color: #1c1f26;
  --button-variant-icon-border-color: #30343c;
  --button-variant-icon-color: rgba(255, 255, 255, 0.87);
  --compact-label-background-color: #1c1f26;
  --compact-label-border-color: #30343c;
  --preview-resize-handle-color-1: #1c1f26;
  --preview-resize-handle-border: #30343c;
  --filemanager-overlay-background: rgba(22, 24, 29, 0.7);

  /* Empty-block placeholders ("Drop your file here") and invalid-module boxes are drawn ON the
     email canvas, which keeps its own (usually white) design — so they keep BEE's light values
     rather than turning into dark boxes inside a white email. */
  --stage-module-placeholder-background: #eeeeee;
  --stage-module-placeholder-color: #404040;
  --stage-module-placeholder-outline: #8c8c8c;
  --stage-module-placeholder-progress-background: #404040;
  --invalid-module-background: #ffffff;
  --invalid-module-color: #595959;

  /* the few light literals BEE hard-codes */
  --sidebar-dropdown-more-items-background: #1c1f26;
  --comment-thread-modal-background: rgba(22, 24, 29, 0.95);
  --generic-box-shadow: rgba(0, 0, 0, 0.4);
  color-scheme: dark;
}
`;

// The hosted stylesheets the landing-page and pop-up editor configs pass to BEE.
export const BEE_CUSTOM_CSS_URL = 'https://www.pulseem.co.il/Pulseem/Css/beefreeRtlFixes.css';
export const BEE_POPUP_CUSTOM_CSS_URL = 'https://www.pulseem.co.il/Pulseem/Css/beefreeRtlFixesPopup.css';

export const isAppDarkMode = (): boolean =>
  typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark';

// For configs that pass a hosted stylesheet URL straight to BEE: in dark mode, wrap it in a
// data: stylesheet that imports the hosted file first and then applies the dark tokens (the
// same data:-URL + @import pattern HtmlCampaign/helper/Config.tsx already ships with).
export const withBeeDarkTheme = (customCssUrl: string): string =>
  isAppDarkMode()
    ? `data:text/css;charset=utf-8,${encodeURIComponent(`@import url('${customCssUrl}');\n${BEE_DARK_CSS}`)}`
    : customCssUrl;

// BEE reads customCss only when it starts: loadConfig({ customCss }), reload() and a second
// start() on the running instance were all tested and none re-apply it, and a cross-origin
// iframe's prefers-color-scheme follows the OS rather than our page. Restarting BEE on every
// toggle would re-run each editor's start-up (incl. the new-campaign auto-save) and drop undo
// history, so instead the app's theme toggle is disabled while a BEE editor is open — BEE always
// starts in, and stays in, the app's current theme.
export const useBeeEditorThemeLock = () => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(setBeeEditorOpen(true));
    return () => {
      dispatch(setBeeEditorOpen(false));
    };
  }, [dispatch]);
};
